// YNX Music Promo Engine — client helpers
import JSZip from 'jszip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const generatePromo = async (input) => {
  const resp = await fetch(`${API}/promo/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      song_title: input.songTitle,
      artist_name: input.artistName,
      genre: input.genre || 'pop',
      mood: input.mood || '',
      audience: input.audience || '',
      countries: input.countries || 'France, Belgique, Suisse, Canada francophone',
      youtube_url: input.youtubeUrl || '',
      streaming_links: input.streamingLinks || '',
      bio: input.bio || '',
      model: input.model || 'gpt-4o',
    }),
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(`Erreur API ${resp.status}: ${t.slice(0, 250)}`);
  }
  return resp.json();
};

const safeFileName = (s) => (s || 'track').replace(/[^a-z0-9_-]+/gi, '-').toLowerCase().slice(0, 40);

const b64ToBytes = (b64) => {
  if (!b64) return null;
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
};

/** Build a .zip of the promo pack and trigger a download. */
export const downloadPromoZip = async (payload) => {
  const zip = new JSZip();
  const slug = safeFileName(`${payload.artist_name}-${payload.song_title}`);
  const plan = payload.plan || {};

  // 1. README
  zip.file('README.md', `# ${payload.artist_name} — ${payload.song_title}\n\nPack promo généré par YNX Music Promo Engine.\n\n## Contenu\n- youtube_seo.md — Titre, description, tags, hashtags, prompt miniature\n- social_posts.md — Captions Instagram / TikTok / Twitter / Facebook\n- shorts_ideas.md — 6 idées de shorts/reels\n- epk.md — Press kit (bios FR/EN + citations)\n- curators.csv — Liste de curateurs playlist\n- outreach_email.txt — Email type à envoyer aux curateurs\n- ads_brief.md — Brief campagne YouTube/Meta Ads\n- content_calendar.csv — Plan 30 jours\n- daily_actions.md — 3 actions à faire aujourd'hui\n- thumbnail.png — Miniature YouTube 16:9\n\nGénéré par YNX 🚀\n`);

  // 2. YouTube SEO
  const yt = plan.youtube_seo || {};
  zip.file('youtube_seo.md', `# YouTube SEO\n\n## Titre (${(yt.title || '').length} caractères)\n${yt.title || '—'}\n\n## Description\n${yt.description || '—'}\n\n## Tags (${(yt.tags || []).length})\n${(yt.tags || []).join(', ')}\n\n## Hashtags\n${(yt.hashtags || []).join(' ')}\n\n## Prompt miniature\n${yt.thumbnail_prompt || '—'}\n`);

  // 3. Social posts
  const sp = plan.social_posts || {};
  const fmt = (arr) => (arr || []).map((x, i) => `${i + 1}. ${x}`).join('\n\n');
  zip.file('social_posts.md', `# Captions multi-plateformes\n\n## Instagram\n${fmt(sp.instagram)}\n\n## TikTok\n${fmt(sp.tiktok)}\n\n## Twitter / X\n${fmt(sp.twitter)}\n\n## Facebook\n${fmt(sp.facebook)}\n\n## Meilleures heures de post\n${sp.best_post_times || '—'}\n`);

  // 4. Shorts ideas
  const shorts = (plan.shorts_ideas || []).map((s, i) => `### Short #${i + 1} (${s.duration || ''})\n- **Hook** : ${s.hook}\n- **Concept** : ${s.concept}\n`).join('\n');
  zip.file('shorts_ideas.md', `# 6 idées de shorts/reels\n\n${shorts || '—'}\n`);

  // 5. EPK
  const epk = plan.epk || {};
  zip.file('epk.md', `# Press Kit (EPK)\n\n## Bio courte (FR)\n${epk.short_bio_fr || '—'}\n\n## Bio longue (FR)\n${epk.long_bio_fr || '—'}\n\n## Short bio (EN)\n${epk.short_bio_en || '—'}\n\n## Citations presse\n> "${epk.press_quote_1 || ''}"\n\n> "${epk.press_quote_2 || ''}"\n\n> "${epk.press_quote_3 || ''}"\n`);

  // 6. Curators CSV
  const curators = plan.playlist_curators || [];
  const csv = ['Name,Platform,Niche,Submit via,Notes', ...curators.map((c) => [c.name, c.platform, c.niche, c.submit_via, c.notes].map((v) => `"${(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
  zip.file('curators.csv', csv);

  // 7. Outreach email
  const oe = plan.outreach_email || {};
  zip.file('outreach_email.txt', `Sujet : ${oe.subject || ''}\n\n${oe.body || ''}\n`);

  // 8. Ads brief
  const ab = plan.ads_brief || {};
  zip.file('ads_brief.md', `# Brief campagne Ads\n\n- **Plateforme** : ${ab.platform || '—'}\n- **Budget test** : ${ab.budget_test || '—'}\n- **Audience** : ${ab.audience || '—'}\n- **Accroche** : ${ab.ad_copy_headline || '—'}\n- **Description** : ${ab.ad_copy_description || '—'}\n- **CPV attendu** : ${ab.expected_cpv || '—'}\n- **KPIs à suivre** : ${(ab.kpi_to_track || []).join(', ')}\n`);

  // 9. Calendar CSV
  const cal = plan.content_calendar_30d || [];
  const calCsv = ['Day,Platform,Format,Action', ...cal.map((c) => [c.day, c.platform, c.format, c.action].map((v) => `"${(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
  zip.file('content_calendar.csv', calCsv);

  // 10. Daily actions
  zip.file('daily_actions.md', `# 3 actions à faire aujourd'hui\n\n${(plan.daily_3_actions || []).map((a, i) => `${i + 1}. ${a}`).join('\n')}\n`);

  // 11. Thumbnail PNG
  const thumb = b64ToBytes(payload.thumbnail_b64);
  if (thumb) zip.file('thumbnail.png', thumb);

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}-promo-pack.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};
